import { z } from "zod";

/** Cross-specialty patient recall (scheduled follow-up / bring-back). */

const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

export const recallStatusSchema = z.enum(["pending", "completed", "cancelled"]);
export type RecallStatus = z.infer<typeof recallStatusSchema>;

export const recallKindSchema = z.enum([
	"checkup",
	"follow_up",
	"lab_recheck",
	"vaccine",
	"other",
]);
export type RecallKind = z.infer<typeof recallKindSchema>;

export const patientRecallApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	/** Denormalized for the clinic-wide list (joined from patients). */
	patient_name: z.string().nullable(),
	patient_phone: z.string().nullable(),
	due_date: dateStr,
	kind: recallKindSchema.nullable(),
	reason: z.string(),
	status: recallStatusSchema,
	notes: z.string().nullable(),
	notified_at: z.string().datetime().nullable(),
	created_at: z.string().datetime(),
});
export type PatientRecallApi = z.infer<typeof patientRecallApiSchema>;

export const createRecallInputSchema = z.object({
	patient_id: z.string().uuid(),
	due_date: dateStr,
	kind: recallKindSchema.optional(),
	reason: z.string().min(1).max(300),
	notes: z.string().max(1000).optional(),
});
export type CreateRecallInput = z.infer<typeof createRecallInputSchema>;

export const updateRecallInputSchema = z.object({
	status: recallStatusSchema.optional(),
	due_date: dateStr.optional(),
	reason: z.string().min(1).max(300).optional(),
	notes: z.string().max(1000).nullable().optional(),
});
export type UpdateRecallInput = z.infer<typeof updateRecallInputSchema>;

export const recallsListQuerySchema = z.object({
	/** 'pending' (default) | 'completed' | 'cancelled' | 'all'. */
	status: z
		.enum(["pending", "completed", "cancelled", "all"])
		.default("pending"),
	/** Only recalls due on/before this date (YYYY-MM-DD). Omit = all. */
	due_before: dateStr.optional(),
	limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type RecallsListQuery = z.infer<typeof recallsListQuerySchema>;
